import React from 'react';
import { DialogTitle, DialogContent, DialogActions, Dialog, dialogClasses, Table, Checkbox, TableBody, TableCell, TableHead, TableRow, TableContainer, TableSortLabel, Typography, } from '@mui/material';
import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import CloseIcon from '@mui/icons-material/Close';
import { stableSort, getComparator, } from '../../../utils/muiTableSorting';
import { ESGFNodeList } from '../../../config/customSelections/datanodes';
const classes = {
    root: {
        width: '100%',
    },
    paper: {
        width: '100%',
        marginBottom: '8px',
    },
    table: {
        minWidth: '500px',
    },
    visuallyHidden: {
        border: '0px',
        clip: 'rect(0 0 0 0)',
        height: '1px',
        margin: '-1px',
        overflow: 'hidden',
        padding: '0px',
        position: 'absolute',
        top: '20px',
        width: '1px',
    },
    dialogPaper: {
        minHeight: '80vh',
        maxHeight: '80vh',
    },
    dialogActions: {
        display: 'flex',
        justifyContent: 'space-around',
        borderTop: '1px solid #BDBDBD',
        paddingTop: '10px',
    },
    dialogTitle: {
        textAlign: 'center',
        boxShadow: '0px 0px 4px 1px #BDBDBD',
    },
    dialogContent: {
        textAlign: 'center',
    },
    closeButton: {
        position: 'absolute',
        right: '5px',
        top: '5px',
        color: 'grey',
    },
    rookFilter: {
        marginLeft: '5px',
        display: 'flex',
        justifyContent: 'center',
        alignContent: 'center',
    },
};
export const rookSupportingNodes = [
    'esgf1.dkrz.de',
    'esgf3.dkrz.de',
    'esgf-node2.cmcc.it',
]; // Register nodes here that support rook wps
export const onlyRookNodesSelected = (selection) => selection.every((val) => rookSupportingNodes.includes(val));
var ROW;
(function (ROW) {
    ROW["NODE"] = "node";
    ROW["SUBSETTING"] = "subsetting";
})(ROW || (ROW = {}));
const EnhancedTableHead = (props) => {
    const { onSelectAllClick, numSelected, rowCount, order, orderBy, onRequestSort, } = props;
    const createSortHandler = (property) => (event) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        onRequestSort(event, property);
    };
    return (React.createElement(TableHead, null,
        React.createElement(TableRow, null,
            React.createElement(TableCell, { padding: 'checkbox' },
                React.createElement(Checkbox, { indeterminate: numSelected > 0 && numSelected < rowCount, checked: rowCount > 0 && numSelected === rowCount, onChange: onSelectAllClick, color: 'primary' })),
            React.createElement(TableCell, { align: 'left' }, "Node"),
            React.createElement(TableCell, { align: 'left', sortDirection: orderBy === ROW.SUBSETTING ? order : false },
                React.createElement(TableSortLabel, { active: orderBy === ROW.SUBSETTING, direction: orderBy === ROW.SUBSETTING ? order : 'asc', onClick: createSortHandler(ROW.SUBSETTING) },
                    "Subsetting Mode",
                    React.createElement("span", { style: classes.visuallyHidden }, order === 'desc' ? 'sorted descending' : 'sorted ascending'))))));
};
const EnhancedTable = ({ rows, localState, setLocalState, }) => {
    const [order, setOrder] = React.useState('desc');
    const [orderBy, setOrderBy] = React.useState(ROW.SUBSETTING);
    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };
    const handleSelectionChange = (event, text) => {
        if (localState.includes(text)) {
            setLocalState(localState.filter((nodes) => nodes !== text));
        }
        else {
            setLocalState([...localState, text]);
        }
    };
    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            setLocalState(ESGFNodeList);
            return;
        }
        setLocalState([]);
    };
    const isSelected = (name) => localState.indexOf(name) !== -1;
    const data = rows.map((row) => ({
        node: row,
        subsetting: rookSupportingNodes.includes(row)
            ? 'Rook WPS'
            : 'Not Available',
    }));
    return (React.createElement("div", { style: classes.root },
        React.createElement(TableContainer, null,
            React.createElement(Table, { sx: classes.table, "aria-labelledby": 'tableTitle', size: 'medium', "aria-label": 'enhanced table' },
                React.createElement(EnhancedTableHead, { numSelected: localState.length, onSelectAllClick: handleSelectAllClick, rowCount: ESGFNodeList.length, order: order, orderBy: orderBy, onRequestSort: handleRequestSort }),
                React.createElement(TableBody, null, stableSort(data, getComparator(order, orderBy)).map((row, index) => {
                    const isItemSelected = isSelected(row.node);
                    const labelId = `enhanced-table-checkbox-${index}`;
                    return (React.createElement(TableRow, { hover: true, style: { backgroundColor: 'white' }, onClick: (event) => handleSelectionChange(event, row.node), role: 'checkbox', "aria-checked": isItemSelected, tabIndex: -1, key: row.node, selected: isItemSelected },
                        React.createElement(TableCell, { padding: 'checkbox' },
                            React.createElement(Checkbox, { checked: isItemSelected, color: 'primary' })),
                        React.createElement(TableCell, { component: 'th', id: labelId, scope: 'row', style: { paddingLeft: 15 } }, row.node),
                        React.createElement(TableCell, { component: 'th', id: labelId, scope: 'row' }, row.subsetting)));
                }))))));
};
export const NodeSelection = ({ setNodes, openNodeSelection, setOpenNodeSelection, }) => {
    const [localState, setLocalState] = React.useState(ESGFNodeList);
    const setRookOnly = (event) => {
        if (event.target.checked) {
            setLocalState(rookSupportingNodes);
            return;
        }
        setLocalState(ESGFNodeList);
    };
    return (React.createElement(Dialog, { maxWidth: 'xl', "aria-labelledby": 'confirmation-dialog-title', open: openNodeSelection, sx: { [`& .${dialogClasses.paper}`]: classes.dialogPaper }, onClose: () => setOpenNodeSelection(false) },
        React.createElement(DialogTitle, { id: 'dialog-title', sx: classes.dialogTitle },
            "Available ESGF Nodes",
            React.createElement(IconButton, { "aria-label": 'close', sx: classes.closeButton, onClick: () => setOpenNodeSelection(false) },
                React.createElement(CloseIcon, null))),
        React.createElement(DialogContent, { sx: classes.dialogContent },
            React.createElement("div", { style: classes.rookFilter },
                React.createElement(Typography, null,
                    React.createElement(Checkbox, { checked: onlyRookNodesSelected(localState), color: 'default', onChange: setRookOnly }),
                    "Select & enable Rook WPS subsetting")),
            React.createElement(EnhancedTable, Object.assign({ rows: ESGFNodeList }, {
                localState,
                setLocalState,
            }))),
        React.createElement(DialogActions, { sx: classes.dialogActions },
            React.createElement(Button, { onClick: () => {
                    setNodes(localState);
                    setOpenNodeSelection(false);
                }, disabled: localState.length < 1, color: 'primary', variant: 'contained' }, "Ok"))));
};
export default EnhancedTable;
